import { Lock, RefreshCw, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { CustomerStatus, CustomerTier, DocumentClassification, DocumentVerificationStatus, SubjectStatus } from '../../api/areas/accounts';
import type { MissingItem } from '../../api/types';
import { Badge, type Tone } from '../../components/Badge';
import { Field, Pager as BlockPager } from '../../components/blocks';
import { TechnicalDetail } from '../../components/States';
import { api } from '../../api';
import { formatMoney } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { cn } from '../../lib/utils';
import { isCode } from '../../lib/labels';
import { missingItemWith } from '../../lib/missingItem';
import { DOSSIER, humanize } from '../domains/status';

/** Status vocabularies map a raw code to a tone; the label comes from `<ns>:enum.<group>.<code>`. */
export type ToneMap = Readonly<Record<string, Tone>>;
type EnumNs = 'accounts' | 'commerce';
type LooseT = (key: string, options?: { defaultValue?: string }) => string;

/**
 * Translates a backend enum value (`enum.<group>.<value>` in the namespace). Values the
 * vocabulary does not know (several are free text in the database) fall back to the
 * humanised raw value, never to a crash or a missing-key marker.
 */
export function useEnumLabel(ns: EnumNs) {
  const { t } = useTranslation(ns);
  const lookup = t as unknown as LooseT;
  return (group: string, value: string | null | undefined): string => {
    if (!value) return '—';
    if (!isCode(value)) return value; // operator text: verbatim
    return lookup(`enum.${group}.${value}`, { defaultValue: lookup(`enum.${group}.${value.toUpperCase()}`, { defaultValue: humanize(value) }) });
  };
}

/** What a dossier still misses (a status change's missing items) in words (lib/missingItem.ts); a server text the console cannot name folded away as the technical detail. */
export function MissingItems({ items }: { items: MissingItem[] }) {
  const { t, i18n } = useTranslation();
  const tr = t as unknown as (key: string) => string;
  const read = items.map((item) => missingItemWith(item, (key) => (i18n.exists(key) ? tr(key) : undefined)));
  const text = read.map((r) => r.text).join(', ');
  const detail = read.flatMap((r) => (r.detail ? [r.detail] : [])).join('\n') || null;
  return detail ? (
    <span className="grid gap-0.5">
      {text}
      <TechnicalDetail detail={detail} />
    </span>
  ) : (
    <>{text}</>
  );
}

/** Status badge: translated label, raw code in `title`, tone from the map (neutral if unknown). */
export function EnumBadge({ ns = 'accounts', group, tones, value }: { ns?: EnumNs; group: string; tones: ToneMap; value: string | null | undefined }) {
  const label = useEnumLabel(ns);
  if (!value) return <Badge tone="neutral">—</Badge>;
  const tone = tones[value] ?? tones[value.toUpperCase()] ?? 'neutral';
  return (
    <Badge tone={tone} title={value}>
      {label(group, value)}
    </Badge>
  );
}

/** The tier catalogue (`customer.tiers`); empty while it loads or for a user who may not read it (`customer.manage`). */
export function useTiers(): readonly CustomerTier[] {
  const result = useApi((signal) => api.listCustomerTiers({ signal }), []);
  return result.status === 'success' ? result.data.items : [];
}

/** A tier's name in the interface language, marked when it is a reseller tier ("hạng đại lý"). */
export function useTierLabel() {
  const { t, i18n } = useTranslation('accounts');
  return (tier: CustomerTier) => `${i18n.language === 'vi' ? tier.name_vi : tier.name_en}${tier.reseller ? ` · ${t('tierReseller')}` : ''}`;
}

/** A customer's tier by name (its code until the catalogue is read), with the code in `title`. */
export function TierText({ code, tiers }: { code: string; tiers: readonly CustomerTier[] }) {
  const label = useTierLabel();
  const tier = tiers.find((x) => x.code === code);
  return <span title={code}>{tier ? label(tier) : code}</span>;
}

/** Plain translated enum value with the raw code in `title`. */
export function EnumText({ ns = 'accounts', group, value }: { ns?: EnumNs; group: string; value: string | null | undefined }) {
  const label = useEnumLabel(ns);
  if (!value) return <span className="text-muted-foreground">—</span>;
  return <span title={value}>{label(group, value)}</span>;
}

export const CUSTOMER_STATUS: Record<CustomerStatus, Tone> = { ACTIVE: 'ok', SUSPENDED: 'serious', CLOSED: 'neutral' };
export const SUBJECT_STATUS: Record<SubjectStatus, Tone> = { ACTIVE: 'ok', MERGED: 'neutral', DEACTIVATED: 'neutral' };
/** `subject.subject_verifications.status`. */
export const VERIFICATION: ToneMap = { UNVERIFIED: 'neutral', PENDING: 'pending', VERIFIED: 'ok', FAILED: 'crit', MISMATCH: 'crit', EXPIRED: 'serious', REVOKED: 'serious' };
export const DOC_VERIFICATION: Record<DocumentVerificationStatus, Tone> = { PENDING: 'pending', PASSED: 'ok', FAILED: 'crit', INCONCLUSIVE: 'warn' };
export const CLASSIFICATION: Record<DocumentClassification, Tone> = { PUBLIC: 'neutral', INTERNAL: 'neutral', CONFIDENTIAL: 'info', HIGHLY_SENSITIVE: 'warn', SECRET: 'crit' };
/** Dossier tones follow the domain pages (one vocabulary, `domain.dossiers.status`). */
export const DOSSIER_STATUS: ToneMap = Object.fromEntries(Object.entries(DOSSIER).map(([k, m]) => [k, m.tone]));

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
}

/**
 * URL-backed filters plus keyset paging (`after` / `next_after`). Changing a filter
 * resets the page; "Previous" walks back through the cursors seen in this tab.
 */
export function useListParams<K extends string>(keys: readonly K[]) {
  const [params, setParams] = useSearchParams();
  const [history, setHistory] = useState<string[]>([]);
  const values = Object.fromEntries(keys.map((k) => [k, params.get(k) ?? ''])) as Record<K, string>;
  const after = params.get('after') ?? '';

  const update = (next: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p);
  };

  return {
    values,
    after,
    /** True when any filter is set (the Reset button shows then). */
    filtered: keys.some((k) => Boolean(values[k])),
    /** Sets filters and goes back to the first page. */
    setFilter: (k: K, v: string) => {
      setHistory([]);
      update({ [k]: v, after: '' });
    },
    setFilters: (next: Partial<Record<K, string>>) => {
      setHistory([]);
      update({ ...next, after: '' } as Record<string, string>);
    },
    reset: () => {
      setHistory([]);
      update(Object.fromEntries([...keys, 'after'].map((k) => [k, ''])));
    },
    next: (cursor: string) => {
      setHistory([...history, after]);
      update({ after: cursor });
    },
    prev: () => {
      update({ after: history[history.length - 1] ?? '' });
      setHistory(history.slice(0, -1));
    },
  };
}

export function Pager({ shown, hasPrev, nextAfter, onPrev, onNext }: { shown: number; hasPrev: boolean; nextAfter: string | null; onPrev: () => void; onNext: (cursor: string) => void }) {
  const { t } = useTranslation('accounts');
  return <BlockPager canPrevious={hasPrev} canNext={Boolean(nextAfter)} onPrevious={onPrev} onNext={() => nextAfter && onNext(nextAfter)} info={t('pager.shown', { count: shown })} />;
}

const ANY = '__any';

/** Labelled select for a FilterBar; the empty value means "any". */
export function FilterSelect({ id, label, value, options, onChange, className }: { id?: string; label: string; value: string; options: readonly (readonly [string, string])[]; onChange: (v: string) => void; className?: string }) {
  const { t } = useTranslation('accounts');
  return (
    <Field label={label} className={cn('w-full sm:w-44', className)}>
      <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? '' : v)}>
        <SelectTrigger id={id} size="sm" className="w-full" aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>{t('filter.any')}</SelectItem>
          {options.map(([v, l]) => (
            <SelectItem key={v} value={v}>
              {l}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

/** A search box that applies on Enter or blur. */
export function FilterText({ id, label, value, placeholder, onApply, className }: { id?: string; label: string; value: string; placeholder?: string; onApply: (v: string) => void; className?: string }) {
  return (
    <Field label={label} className={cn('w-full sm:w-64', className)}>
      <Input
        id={id}
        key={value}
        type="search"
        className="h-8"
        defaultValue={value}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        onKeyDown={(e) => e.key === 'Enter' && onApply(e.currentTarget.value.trim())}
        onBlur={(e) => e.target.value.trim() !== value && onApply(e.target.value.trim())}
      />
    </Field>
  );
}

/** A removable chip for an ID filter that only arrives through links (e.g. `customer_id`). */
export function FilterChip({ label, title, className, onClear }: { label: string; title?: string; className?: string; onClear: () => void }) {
  const { t } = useTranslation('accounts');
  return (
    <Button type="button" variant="secondary" size="sm" onClick={onClear} title={title} aria-label={t('filter.clearFilter', { label })} className={className}>
      {/* Truncates only when `className` caps the width (`max-w-full`). */}
      <span className="min-w-0 truncate">{label}</span>
      <X aria-hidden="true" />
    </Button>
  );
}

/** Reset-all button for a FilterBar, shown only while a filter is set. */
export function ResetFilters({ show, onReset }: { show: boolean; onReset: () => void }) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <Button type="button" variant="ghost" size="sm" onClick={onReset}>
      <X aria-hidden="true" /> {t('action.reset')}
    </Button>
  );
}

export function RefreshButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClick}>
      <RefreshCw aria-hidden="true" /> {t('action.refresh')}
    </Button>
  );
}

/** Subtitle with inline `<code>` markup from the translation. */
export function RichText({ ns = 'accounts', k, values }: { ns?: EnumNs; k: string; values?: Record<string, unknown> }) {
  return <Trans ns={ns} i18nKey={k as never} values={values} components={{ code: <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs" /> }} />;
}

/** A masked personal-data hint, visibly marked as masked. */
export function Masked({ value, tagged = false }: { value: string | null | undefined; tagged?: boolean }) {
  const { t } = useTranslation('accounts');
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5" title={t('common.maskedHint')}>
      <Lock className="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="font-mono text-sm">{value}</span>
      {tagged ? (
        <Badge tone="neutral" icon={false}>
          {t('common.masked')}
        </Badge>
      ) : (
        <span className="sr-only">({t('common.masked')})</span>
      )}
    </span>
  );
}

/** Shared cell style helpers so tables look the same across pages. */
export const cellLink = 'font-medium text-primary hover:underline';
export const mutedSmall = 'text-xs text-muted-foreground';

/** A list cell with the record's type and, under it, its country code (keeps wide lists within the page). */
export function TypeCountry({ type, country }: { type: ReactNode; country: string | null | undefined }) {
  return (
    <div className="space-y-0.5">
      {type}
      <div className={mutedSmall}>{country ?? '—'}</div>
    </div>
  );
}

export function Stack({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-col gap-6', className)}>{children}</div>;
}

/** Exact amount in its currency (the raw decimal string is in the tooltip). */
export function MoneyText({ amount, currency, className }: { amount: string | null | undefined; currency: string | null | undefined; className?: string }) {
  if (amount === null || amount === undefined || !currency) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={cn('whitespace-nowrap tabular-nums', className)} title={`${amount} ${currency}`}>
      {formatMoney({ amount, currency })}
    </span>
  );
}

/** Paths of the records this area links to. */
export const paths = {
  customer: (id: string) => `/customers/${encodeURIComponent(id)}`,
  customerUser: (customerId: string, userId: string) => `/customers/${encodeURIComponent(customerId)}/users/${encodeURIComponent(userId)}`,
  subject: (id: string) => `/subjects/${encodeURIComponent(id)}`,
  dossier: (id: string) => `/dossiers/${encodeURIComponent(id)}`,
  document: (id: string) => `/documents/${encodeURIComponent(id)}`,
  domain: (asciiName: string) => `/domains/${encodeURIComponent(asciiName)}`,
  order: (id: string) => `/orders/${encodeURIComponent(id)}`,
  invoice: (id: string) => `/invoices/${encodeURIComponent(id)}`,
  payment: (id: string) => `/payments/${encodeURIComponent(id)}`,
  ledgerAccount: (id: string) => `/ledger/accounts/${encodeURIComponent(id)}`,
  abuseCase: (id: string) => `/compliance/icann/abuse/${encodeURIComponent(id)}`,
};

type LookupKind = 'customer' | 'subject';

/**
 * The readable name of a record that arrives in the URL only as an ID (a filter set by a
 * link, e.g. `?customer_id=`). `known` (a name already on screen) wins; otherwise the record
 * is fetched. Shows an ellipsis while loading and a neutral label when it cannot be read.
 */
export function useRecordName(kind: LookupKind, id: string, known?: string | null): string {
  const { t } = useTranslation('accounts');
  const result = useApi(
    (signal): Promise<string | null> => {
      if (!id || known) return Promise.resolve(null);
      if (kind === 'customer') return api.getCustomerOverview(id, { signal }).then((r) => r.customer.display_name);
      return api.getSubject(id, { signal }).then((s) => s.versions?.find((v) => v.id === s.current_version_id)?.legal_name ?? null);
    },
    [kind, id, known],
  );
  if (known) return known;
  if (result.status === 'success' && result.data) return result.data;
  return result.status === 'loading' ? '…' : t('filter.selectedRecord');
}
